import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useId, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { Input } from '../../../../components/Input';
import { MOD_KEY } from '../../../../components/Kbd';
import { Popover } from '../../../../components/Popover';
import { anchorFrom, type AnchorRect } from '../../../../lib/anchored-panel';
import { useFocusOnOpen } from '../../../../lib/use-focus-on-open';
import { findInAdmin, type AdminMenuView, type FindResult } from '../../admin-sections';
import styles from './AdminFind.module.css';

/** Wider than the menu, so a setting and the section it sits in read on one line (admin-frame.html, Option A). */
const RESULTS_WIDTH = 440;

interface AdminFindProps {
  readonly menu: AdminMenuView;
  /** In the phone sheet and the rail's pop-up the results sit under the field instead of in a pop-up of their own. */
  readonly isInline?: boolean;
  /** Ctrl+/ puts the cursor here (FRAME-SPEC §2). */
  readonly inputRef?: RefObject<HTMLInputElement | null>;
  readonly autoFocus?: boolean;
  /** Called once a result is opened, so the sheet or pop-up holding the field can close. */
  readonly onOpened?: () => void;
}

/**
 * "Find a setting": section names, the words people look for them by, and settings inside them. A combobox: the
 * cursor stays in the field while Up and Down move through the results and Enter opens one (06-qol §1's keys).
 */
export function AdminFind({ menu, isInline = false, inputRef, autoFocus = false, onOpened }: AdminFindProps): ReactNode {
  const navigate = useNavigate();
  const ownInputRef = useRef<HTMLInputElement | null>(null);
  const fieldRef = inputRef ?? ownInputRef;
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  useFocusOnOpen(fieldRef, autoFocus);

  const results = findInAdmin(query, menu);
  const hasQuery = query.trim() !== '';
  const isShowing = hasQuery && (isInline || anchor !== null);
  const active = Math.min(activeIndex, Math.max(0, results.length - 1));

  const show = (): void => {
    if (!isInline && wrapRef.current) setAnchor(anchorFrom(wrapRef.current));
  };

  const open = (result: FindResult): void => {
    setQuery('');
    setAnchor(null);
    if (result.section.isExternal === true) {
      // A different application (Bull Board), so a new tab, as the menu's own link does.
      window.open(result.section.to, '_blank', 'noopener,noreferrer');
    } else {
      void navigate({ to: result.section.to });
    }
    onOpened?.();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape' && hasQuery) {
      // A pop-up of results closes itself on Esc first; with none showing, Esc clears the field.
      event.preventDefault();
      setQuery('');
      return;
    }
    if (!hasQuery || results.length === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isShowing) show();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((active + step + results.length) % results.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const result = results[active];
      if (result) open(result);
    }
  };

  const list = (
    <ResultList id={listId} query={query} results={results} activeIndex={active} onHover={setActiveIndex} onOpen={open} />
  );

  return (
    <div className={styles.find}>
      <div ref={wrapRef}>
        <Input
          ref={fieldRef}
          icon={Search}
          type="search"
          placeholder="Find a setting"
          aria-label="Find a setting"
          role="combobox"
          aria-expanded={isShowing}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={isShowing && results.length > 0 ? optionId(listId, active) : undefined}
          autoComplete="off"
          value={query}
          suffix={isInline ? undefined : `${MOD_KEY} /`}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(0);
            if (event.target.value.trim() !== '') show();
          }}
          onFocus={() => {
            if (hasQuery) show();
          }}
          onClick={() => {
            if (hasQuery) show();
          }}
          onKeyDown={onKeyDown}
          onClear={() => setQuery('')}
        />
      </div>

      {isInline ? (
        hasQuery && list
      ) : (
        <Popover anchor={hasQuery ? anchor : null} onClose={() => setAnchor(null)} width={RESULTS_WIDTH} label="Find a setting">
          {list}
        </Popover>
      )}
    </div>
  );
}

function optionId(listId: string, index: number): string {
  return `${listId}-option-${index}`;
}

interface ResultListProps {
  readonly id: string;
  readonly query: string;
  readonly results: readonly FindResult[];
  readonly activeIndex: number;
  readonly onHover: (index: number) => void;
  readonly onOpen: (result: FindResult) => void;
}

const BLOCKS: readonly { readonly kind: FindResult['kind']; readonly title: string }[] = [
  { kind: 'section', title: 'Sections' },
  { kind: 'setting', title: 'Settings inside sections' },
];

function ResultList({ id, query, results, activeIndex, onHover, onOpen }: ResultListProps): ReactNode {
  if (results.length === 0) {
    return (
      <div id={id} role="listbox" aria-label="Settings found" className={styles.results}>
        <p className={styles.empty}>Nothing in Admin matches “{query.trim()}”.</p>
      </div>
    );
  }

  return (
    <div id={id} role="listbox" aria-label="Settings found" className={styles.results}>
      {BLOCKS.map((block) => {
        const rows = results.map((result, index) => ({ result, index })).filter((row) => row.result.kind === block.kind);
        if (rows.length === 0) return null;
        const headId = `${id}-${block.kind}`;
        return (
          <div key={block.kind} role="group" aria-labelledby={headId} className={styles.block}>
            <div id={headId} className={styles.blockHead} role="presentation">
              {block.title}
            </div>
            {rows.map(({ result, index }) => (
              <div
                key={`${result.section.id}-${result.label}`}
                id={optionId(id, index)}
                role="option"
                aria-selected={index === activeIndex}
                className={index === activeIndex ? `${styles.option} ${styles.optionActive}` : styles.option}
                // The cursor stays in the field: a press here must not blur it before the click lands.
                onPointerDown={(event) => event.preventDefault()}
                onMouseEnter={() => onHover(index)}
                onClick={() => onOpen(result)}
              >
                <span className={styles.optionText}>
                  <span className={styles.optionLabel}>{result.label}</span>
                  <span className={styles.optionHint}>
                    {result.kind === 'section' ? result.section.hint : `In ${result.section.label}`}
                  </span>
                </span>
                {result.kind === 'section' && <span className={styles.optionWhere}>{result.where}</span>}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
